/**
 * Sheet-frame band geometry: the one explicit boundary between the engine's
 * bbox form and the viewer's.
 *
 * Slice 2 design review §5.2 requires "an explicit named corner→xywh converter"
 * at exactly one place, because §1.6 (S5) recorded two live coordinate hazards
 * on this path. Both are addressed here, and neither is addressed by reusing
 * `DrawingViewer.irBboxToCanvas`:
 *
 *   1. FORM. Engine bboxes are CORNERS `[x0, y0, x1, y1]` (`sheet_frame.py`
 *      `_strip_bbox`). `irBboxToCanvas` expects XYWH. Feeding one to the other
 *      keeps the top-left corner and reads `[x1, y1]` as `[w, h]`, which is the
 *      "boxes render into white space" symptom Paige observed on a single-sheet
 *      drawing.
 *
 *   2. BOUNDS. `irBboxToCanvas` reads `ir.pageSize`, which `pdf_extract.py`
 *      `_build_ir_skeleton` sets ONCE from page 0 for the whole document. A
 *      proposal on a page of a different size is then silently mis-transformed.
 *      `sheetFrameBandToCanvas` takes `pageEvidence` and has no access to `ir`
 *      at all, so the document-level value is structurally unreachable rather
 *      than merely unused.
 *
 * ORIENTATION. Sheet-frame bboxes need NO y-flip, which is the opposite of
 * `irBboxToCanvas` and worth stating explicitly because the two functions sit
 * next to each other. `sheet_frame.py:394` (`_page_display_matrix`) maps raw
 * coordinates into "displayed, bottom-left-origin PDF space", and the
 * convention pinned at `sheet_frame.py:729` is that "the visually-TOP edge sits
 * at the LARGER y bound … high y is up" — guarded engine-side by
 * `test_top_bottom_orientation_matches_page_bounds_convention`. That is already
 * PDF user space, which is exactly what pdf.js `convertToViewportRectangle`
 * consumes. IR entity bboxes are top-left/y-down and DO need the flip; these do
 * not.
 *
 * Everything here fails closed by returning `null`. A band that cannot be
 * transformed with confidence must not be drawn: this overlay's entire purpose
 * is to show a reviewer which region a decision would apply to, and a
 * confidently-drawn wrong region is worse than no region at all.
 */

/** The engine-wide bbox form for sheet-frame geometry (canonical plan §4.2). */
export const SHEET_FRAME_BBOX_FORM = 'corners';

/**
 * Page size agreement tolerance, in PDF points.
 *
 * `sheet_frame_evidence.compute_evidence_identity` stores
 * `displayedPageBounds` through `normalized_round`, so the stored extent can
 * differ from pdf.js's unrounded page size by a sub-point amount. One point is
 * comfortably above that and far below any real page-size difference (the
 * smallest ISO/ANSI step is tens of points).
 */
const PAGE_SIZE_TOLERANCE_PT = 1.0;

/** How far outside its page bounds a band may sit before it is rejected. */
const CONTAINMENT_TOLERANCE_PT = 0.5;

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * A well-formed corner bbox: four finite numbers with STRICTLY positive extent
 * in both axes.
 *
 * Strict is deliberate. A zero-extent band is degenerate — it would paint a
 * hairline the reviewer could read as a real region — and a negative extent
 * means x1/y1 arrived swapped, which is a producer defect this consumer must
 * not paper over by silently normalising it.
 */
export function isValidCornerBbox(bbox) {
  if (!Array.isArray(bbox) || bbox.length < 4) return false;
  const [x0, y0, x1, y1] = bbox;
  if (!isFiniteNumber(x0) || !isFiniteNumber(y0)) return false;
  if (!isFiniteNumber(x1) || !isFiniteNumber(y1)) return false;
  return x1 > x0 && y1 > y0;
}

/** A well-formed `{ x, y, w, h }` rect: four finite numbers, positive extent. */
export function isValidXywhRect(rect) {
  if (!rect || typeof rect !== 'object') return false;
  const { x, y, w, h } = rect;
  if (!isFiniteNumber(x) || !isFiniteNumber(y)) return false;
  if (!isFiniteNumber(w) || !isFiniteNumber(h)) return false;
  return w > 0 && h > 0;
}

/**
 * Corners `[x0, y0, x1, y1]` → `{ x, y, w, h }`. `null` when the input is not a
 * valid corner bbox.
 */
export function cornersToXywh(bbox) {
  if (!isValidCornerBbox(bbox)) return null;
  const [x0, y0, x1, y1] = bbox;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/**
 * `{ x, y, w, h }` → corners `[x0, y0, x1, y1]`. `null` when the input is not a
 * valid rect. Exact inverse of `cornersToXywh` for every valid input.
 */
export function xywhToCorners(rect) {
  if (!isValidXywhRect(rect)) return null;
  const { x, y, w, h } = rect;
  return [x, y, x + w, y + h];
}

/**
 * Why a band cannot be drawn on the sheet.
 *
 * A fixed enum rather than a boolean, because the panel has to disable a
 * reviewer's controls on the strength of it and "we cannot show you this"
 * deserves a specific reason.
 */
export const BAND_DISPLAYABLE = null;
export const BAND_NO_EVIDENCE = 'no_page_evidence';
export const BAND_INVALID_BBOX = 'invalid_band_geometry';
export const BAND_INVALID_PAGE_BOUNDS = 'invalid_page_bounds';
export const BAND_OUTSIDE_PAGE = 'band_outside_page_bounds';
export const BAND_ROTATED_PAGE = 'rotated_page';

/**
 * Can this band be drawn at all, judged from the page evidence alone?
 *
 * Split out from `sheetFrameBandToCanvas` so the review panel can reach the
 * same verdict WITHOUT a pdf.js viewport, which it does not have. That sharing
 * is the point: a band the viewer will refuse to draw must not be a band the
 * panel offers to confirm. Duplicating these rules in the panel would let the
 * two drift, and the direction of drift that matters is the one where the panel
 * stays permissive after the viewer turns strict.
 *
 * Deliberately excludes the viewport cross-check in `sheetFrameBandToCanvas`,
 * which compares the RENDERED page against this evidence. That is a property of
 * what the viewer currently has open, not of the band.
 *
 * @returns {string|null} `null` when the band is displayable, else a reason.
 */
export function bandDisplayability(bbox, pageEvidence) {
  if (!pageEvidence || typeof pageEvidence !== 'object') return BAND_NO_EVIDENCE;
  if (!isValidCornerBbox(bbox)) return BAND_INVALID_BBOX;

  const bounds = boundsExtent(pageEvidence.displayedPageBounds);
  if (!bounds) return BAND_INVALID_PAGE_BOUNDS;
  if (!isContained(bbox, bounds)) return BAND_OUTSIDE_PAGE;

  // Rotated pages are refused rather than approximated -- see the ROTATION note
  // on `sheetFrameBandToCanvas`. `rotation` is required, not defaulted: absent
  // rotation means this is not the evidence block the API serves, and assuming
  // 0 would be assuming the safe case on the strength of missing data.
  if (pageEvidence.rotation !== 0) return BAND_ROTATED_PAGE;

  return BAND_DISPLAYABLE;
}

function boundsExtent(displayedPageBounds) {
  if (!isValidCornerBbox(displayedPageBounds)) return null;
  const [x0, y0, x1, y1] = displayedPageBounds;
  return { x0, y0, x1, y1, width: x1 - x0, height: y1 - y0 };
}

function isContained(bbox, bounds) {
  const t = CONTAINMENT_TOLERANCE_PT;
  return (
    bbox[0] >= bounds.x0 - t
    && bbox[1] >= bounds.y0 - t
    && bbox[2] <= bounds.x1 + t
    && bbox[3] <= bounds.y1 + t
  );
}

/**
 * Does the page pdf.js is rendering agree with the page the detector measured?
 *
 * `viewport.width`/`height` are scaled and rotation-applied, so dividing by
 * `viewport.scale` recovers the displayed page extent in points — directly
 * comparable to `displayedPageBounds`, which the engine also records after
 * applying /Rotate.
 *
 * This is the check that makes the per-page requirement enforceable rather than
 * merely intended: if a caller ever supplies evidence from the wrong page, the
 * extents disagree and nothing is drawn.
 */
function viewportMatchesBounds(viewport, bounds) {
  const scale = viewport.scale;
  if (!isFiniteNumber(scale) || scale <= 0) return false;
  if (!isFiniteNumber(viewport.width) || !isFiniteNumber(viewport.height)) return false;
  const width = viewport.width / scale;
  const height = viewport.height / scale;
  return (
    Math.abs(width - bounds.width) <= PAGE_SIZE_TOLERANCE_PT
    && Math.abs(height - bounds.height) <= PAGE_SIZE_TOLERANCE_PT
  );
}

/**
 * One sheet-frame band, in canvas coordinates, or `null`.
 *
 * @param {number[]} bbox corner bbox from a `proposed_excluded_band` proposal
 * @param {object} pageEvidence that page's `pageEvidence` block from the GET —
 *   `displayedPageBounds` and `rotation`. Deliberately the whole evidence
 *   object rather than loose bounds, so a caller cannot reach for `ir.pageSize`
 *   and still satisfy the signature.
 * @param {object} viewport a pdf.js `PageViewport`
 * @returns {{x:number,y:number,w:number,h:number}|null}
 *
 * Returns `null` — draw nothing — when:
 *   · the bbox is malformed, non-finite, zero-width or negative-extent;
 *   · `displayedPageBounds` is malformed or degenerate;
 *   · the band does not lie within its own page bounds;
 *   · the page is rotated (see below);
 *   · the rendered page size disagrees with the recorded page size;
 *   · pdf.js returns a non-finite rectangle.
 *
 * ROTATION. Rotated pages are refused rather than approximated. The engine
 * pre-applies /Rotate into the coordinates it stores, while pdf.js applies
 * rotation inside `convertToViewportRectangle`; passing pre-rotated
 * coordinates through would rotate them twice and land the band on the wrong
 * edge of the sheet. Getting that right needs a real rotated fixture to verify
 * against, which does not exist yet, so this fails closed instead of guessing.
 */
export function sheetFrameBandToCanvas(bbox, pageEvidence, viewport) {
  // The evidence-only preconditions, shared verbatim with the review panel so
  // the panel can never offer a decision on a band this function will refuse.
  if (bandDisplayability(bbox, pageEvidence) !== BAND_DISPLAYABLE) return null;
  if (!viewport || typeof viewport.convertToViewportRectangle !== 'function') return null;

  const bounds = boundsExtent(pageEvidence.displayedPageBounds);
  if (!viewportMatchesBounds(viewport, bounds)) return null;

  // No y-flip — see the ORIENTATION note above. The bbox is already in the
  // bottom-left-origin PDF space this function's argument expects.
  const screenRect = viewport.convertToViewportRectangle([bbox[0], bbox[1], bbox[2], bbox[3]]);
  if (!Array.isArray(screenRect) || screenRect.length < 4) return null;
  if (!screenRect.slice(0, 4).every(isFiniteNumber)) return null;

  // pdf.js may return the rectangle with either corner first depending on
  // rotation and flip, so the min/max normalisation is not optional.
  const x = Math.min(screenRect[0], screenRect[2]);
  const y = Math.min(screenRect[1], screenRect[3]);
  const w = Math.abs(screenRect[2] - screenRect[0]);
  const h = Math.abs(screenRect[3] - screenRect[1]);
  if (!(w > 0) || !(h > 0)) return null;
  return { x, y, w, h };
}
